/**
 * Filter group configuration — maps Merchology's CURRENT product data model
 * (vendor / productType / native price / tags) onto Storefront `ProductFilter`s.
 *
 * Nothing here requires a data migration. Groups typed `vendor`, `productType`,
 * and `price` are distinct native filter types (Tier 1: AND-across in one query).
 * Groups typed `tag` are the existing `global-tags.liquid` tags; at most one is
 * pushed to the API per query, the rest resolve via client-side refinement (Tier 2).
 *
 * When a group later moves to a metafield (see the migration doc), change its
 * `type`/builder here — no other layer changes.
 */

import { FilterGroupConfig, PriceRange, ProductFilterInput } from './types';

export const COLOR_TAG_SENTINEL = 'FILTER';

/**
 * Catch-all for product tags the configuration doesn't model.
 *
 * Shopify's native path filtering worked on ANY tag, and the client has links that use
 * plain ones — `/collections/gifts-under-25/electronics` filters on the `Electronics`
 * tag, which belongs to no filter group here. Such a tag has no sidebar control to
 * contradict, so it's carried as itself: `{ tag: "Electronics" }` to the API, an
 * applied-filter chip in the UI, and `filter.p.tag=Electronics` in the URL. Without it
 * those links would quietly widen to the whole collection.
 *
 * Empty `tagPrefix`, so `tagForValue` round-trips the tag unchanged. It is deliberately
 * absent from collection-filter-config.liquid: there's no vocabulary to list, so it
 * renders no sidebar group — only a chip, which is what removes it.
 */
export const LEGACY_TAG_GROUP_KEY = 'legacy_tag';

/**
 * Price bucket label -> native range. A subset of `price_tags` in global-tags.liquid.
 *
 * `Under $10` is offered because the bounded buckets start at $10 and collections do carry
 * products below it — without it those products are unreachable by price. Its open lower
 * bound is left implicit (`priceInAnyRange` and `mergePriceRanges` both read a missing
 * `min` as 0).
 *
 * `Under $5` and `Under $25` stay out: they nest inside `Under $10` and `$10 to $25`, so
 * multi-select couldn't express anything the offered buckets don't already cover — picking
 * `Under $25` alongside `$10 to $25` just re-selects the same products. Keys must stay in
 * sync with the price options in collection-filter-config.liquid.
 */
export const PRICE_RANGES: Record<string, PriceRange> = {
  'Under $10': { max: 10 },
  '$10 to $25': { min: 10, max: 25 },
  '$25 to $50': { min: 25, max: 50 },
  '$50 to $100': { min: 50, max: 100 },
  '$100 to $250': { min: 100, max: 250 },
  '$250+': { min: 250 },
};

/**
 * Canonical text size -> every variant-option spelling that means it. The Size variant
 * data is inconsistent (XS / X-Small / Extra Small; 2XL / XX-Large / 2X-Large …), so a
 * canonical option expands to all of these OR'd together. Numeric sizes (waist/shoe)
 * are intentionally not normalized.
 */
export const SIZE_SYNONYMS: Record<string, string[]> = {
  XXS: ['XXS', 'XX-Small', '2X-Small', '2XS'],
  XS: ['XS', 'X-Small', 'X-small', 'Extra Small'],
  S: ['S', 'Small'],
  M: ['M', 'Medium', 'M-Tall'],
  L: ['L', 'Large', 'L-Tall', 'Large-Tall', 'Large Tall'],
  XL: ['XL', 'X-Large', 'X-large', '1X-Large', 'Extra Large', 'XL-Tall', 'X-Large-Tall', 'X-Large Tall'],
  '2XL': ['2XL', 'XXL', 'XX-Large', '2X-Large', '2x-Large', '2X-large', '2XL-Tall', '2X-Large-Tall', '2X-Large Tall'],
  '3XL': ['3XL', 'XXXL', '3X-Large', '3X-large', '3x-Large', '3XL-Tall', '3X-Large-Tall', '3X-Large Tall'],
  '4XL': ['4XL', '4X-Large', '4X-large', '4x-Large', '4XL-Tall', '4X-Large-Tall', '4X-Large Tall'],
  '5XL': ['5XL', '5X-Large', '5X-large', '5x-Large', '5XL-Tall', '5X-Large-Tall'],
  '6XL': ['6XL', '6X-Large', '6X-large', '6x-Large', '6XL-Tall', '6x-Large-Tall', '6X-Large-Tall'],
};

export const FILTER_GROUPS: FilterGroupConfig[] = [
  { key: 'brand', label: 'Brand', type: 'vendor' },
  { key: 'product_type', label: 'Product Type', type: 'productType' },
  { key: 'price', label: 'Price', type: 'price' },
  // The Storefront variant option is named `size` (lowercase) — the facet input and the
  // native `filter.v.option.size` param both use it, so the name must match exactly or
  // counts miss and the filter no-ops.
  { key: 'size', label: 'Extended Sizes', type: 'variantOption', optionName: 'size', valueSynonyms: SIZE_SYNONYMS },
  { key: 'color', label: 'Color', type: 'tag', tagPrefix: COLOR_TAG_SENTINEL, primaryRank: 10 },
  { key: 'material', label: 'Material', type: 'tag', tagPrefix: 'material=', primaryRank: 20 },
  { key: 'style', label: 'Style', type: 'tag', tagPrefix: 'style=', primaryRank: 30 },
  { key: 'feature', label: 'Feature', type: 'tag', tagPrefix: 'feature=', primaryRank: 40 },
  { key: 'fit', label: 'Fit', type: 'tag', tagPrefix: 'fit=', primaryRank: 50 },
  { key: 'gender', label: 'Gender', type: 'tag', tagPrefix: 'gender=', primaryRank: 60 },
  // Sizing *category* (e.g. `standard`), not garment size — that's the `size` group.
  { key: 'sizing', label: 'Sizing', type: 'tag', tagPrefix: 'sizing=', primaryRank: 70 },
  { key: 'occasion', label: 'Occasion', type: 'tag', tagPrefix: 'occasion=', primaryRank: 80 },
  { key: 'csr', label: 'CSR', type: 'tag', tagPrefix: 'csr=', primaryRank: 90 },
  { key: 'decoration', label: 'Decoration', type: 'tag', tagPrefix: 'decoration=', primaryRank: 100 },
  { key: 'shop_by', label: 'Shop By', type: 'tag', tagPrefix: 'shop_by=', primaryRank: 110 },
  { key: 'quick_ship', label: 'Quick Ship', type: 'tag', tagPrefix: 'quick_ship=', primaryRank: 120 },
  { key: 'minimum', label: 'Minimum Qty', type: 'tag', tagPrefix: 'minimum=', primaryRank: 130 },
  { key: 'dimensions', label: 'Dimensions', type: 'tag', tagPrefix: 'dimensions=', primaryRank: 140 },
  { key: 'capacity', label: 'Capacity', type: 'tag', tagPrefix: 'capacity=', primaryRank: 150 },
  { key: 'industry', label: 'Industry', type: 'tag', tagPrefix: 'industry=', primaryRank: 160 },
  // Internal-only; gate its display to employees in the UI layer.
  { key: 'supplier', label: 'Supplier', type: 'tag', tagPrefix: 'supplier=', primaryRank: 170 },
  // Lowest rank: an unmodelled tag is usually the most selective thing in the selection
  // and carries no facet count to lose, so it's the one worth spending the API tag slot on.
  { key: LEGACY_TAG_GROUP_KEY, label: 'Tag', type: 'tag', tagPrefix: '', primaryRank: 5 },
];

const GROUPS_BY_KEY: Record<string, FilterGroupConfig> = FILTER_GROUPS.reduce(
  (acc, g) => {
    acc[g.key] = g;
    return acc;
  },
  {} as Record<string, FilterGroupConfig>
);

export function getGroup(key: string): FilterGroupConfig | undefined {
  return GROUPS_BY_KEY[key];
}

export const EXCLUDED_TAG = 'DISCONTINUED';

export function tagForValue(group: FilterGroupConfig, value: string): string {
  if (group.tagPrefix === COLOR_TAG_SENTINEL) {
    return 'Filter' + value.replace(/\s+/g, '');
  }
  return (group.tagPrefix || '') + value;
}

export function resolveTag(tag: string): { group: FilterGroupConfig; value: string } | null {
  if (tag.indexOf('Filter') === 0) {
    const color = FILTER_GROUPS.find((g) => g.tagPrefix === COLOR_TAG_SENTINEL);
    if (color) return { group: color, value: tag.replace(/^Filter/, '') };
  }
  const eq = tag.indexOf('=');
  if (eq > -1) {
    const prefix = tag.slice(0, eq + 1);
    const group = FILTER_GROUPS.find((g) => g.tagPrefix === prefix);
    if (group) return { group, value: tag.slice(eq + 1) };
  }
  // Not one of ours — keep it as a plain product tag rather than losing the filter.
  const legacy = GROUPS_BY_KEY[LEGACY_TAG_GROUP_KEY];
  return legacy ? { group: legacy, value: tag } : null;
}

export function nativeFilterForValue(
  group: FilterGroupConfig,
  value: string
): ProductFilterInput | null {
  switch (group.type) {
    case 'vendor':
      return { productVendor: value };
    case 'productType':
      return { productType: value };
    case 'price': {
      const range = PRICE_RANGES[value];
      if (!range) return null;
      const price: { min?: number; max?: number } = {};
      if (typeof range.min === 'number') price.min = range.min;
      if (typeof range.max === 'number') price.max = range.max;
      return { price };
    }
    case 'variantOption':
      if (!group.optionName) return null;
      return { variantOption: { name: group.optionName, value: value } };
    default:
      return null;
  }
}

export function nativeFiltersForValue(
  group: FilterGroupConfig,
  value: string
): ProductFilterInput[] {
  if (group.type === 'variantOption' && group.optionName) {
    const spellings = (group.valueSynonyms && group.valueSynonyms[value]) || [value];
    return spellings.map((s) => ({ variantOption: { name: group.optionName as string, value: s } }));
  }
  const single = nativeFilterForValue(group, value);
  return single ? [single] : [];
}

export function facetKeysForValue(group: FilterGroupConfig, value: string): string[] {
  if (group.type === 'variantOption' && group.optionName) {
    const spellings = (group.valueSynonyms && group.valueSynonyms[value]) || [value];
    return spellings.map((s) => 'variantOption:' + group.optionName + ':' + s);
  }
  switch (group.type) {
    case 'vendor':
      return ['productVendor:' + value];
    case 'productType':
      return ['productType:' + value];
    case 'tag':
      return ['tag:' + tagForValue(group, value)];
    default:
      return [];
  }
}
